import {
  formatMediaPlaybackTime,
  type MediaTimelineDataTrack,
  type MediaTimelineSegment,
} from "@carbon6/c6-frontend-design";
import type { QualityCandidateCheckResp } from "@/api/diagnostics";
import { formatDateTime, parseUtcMs } from "@/utils/datetime";
import { formatEpisodeDuration } from "../../datasetFormatters";
import type { ActionSourceBand, ActionSourceModel } from "./actionSourceSegments";
import { actionSourceTerm, type ActionSourceDataVizKey } from "./actionSourceTerms";
import { FREEZE_CANDIDATE_NAME, type QualityCandidateSpan } from "./qualityCandidates";

/**
 * 출처 띠와 영상 정지 후보를 영상 타임라인 행으로 바꾼다. 진단 쪽에서 c6 타임라인 타입을 아는
 * 유일한 자리다.
 *
 * 띠 시각은 에피소드 기준 초라서 영상 시각으로 그대로 쓴다. 창원 실측에서 mp4 는 30 fps 로
 * 0초에서 시작하고 카메라 첫 기록은 −0.06~+0.02초였다. 그래서 ±2 프레임 안에서 맞는다.
 * 음수 시작만 0 으로 바꾼다.
 */

/**
 * 구간을 싣는 타임라인 행. c6 의 `track` 행은 `segments` 가 선택이다. 이 타입은 `segments` 를 필수로
 * 둔다. 그래서 빌더가 구간을 빠뜨리면 타입 검사가 잡는다.
 */
export type SegmentTimelineTrack = MediaTimelineDataTrack & {
  segments: readonly MediaTimelineSegment[];
};

export const ACTION_SOURCE_TRACK_ID = "action-source";
export const QUALITY_CANDIDATE_TRACK_ID = "quality-candidate";

const TONE: Record<ActionSourceDataVizKey, MediaTimelineSegment["tone"]> = {
  gray: "dataViz.gray",
  orange: "dataViz.orange",
  violet: "dataViz.violet",
  green: "dataViz.green",
  red: "dataViz.red",
};

// 시각 문구가 읽는 구간 모양. 출처 띠와 품질 후보 구간이 같은 형식을 쓴다.
type TimeSpan = Pick<ActionSourceBand, "startSeconds" | "endSeconds">;

/** 띠의 영상 시각 범위. 재생 바 표기(`00:12.3`)와 같은 형식이라서 둘을 바로 비교할 수 있다. */
export function bandClockText(band: TimeSpan): string {
  return `${formatMediaPlaybackTime(Math.max(0, band.startSeconds))}–${formatMediaPlaybackTime(
    band.endSeconds,
  )}`;
}

/**
 * 띠의 길이. 1초 미만은 소수 둘째 자리까지 쓴다. 한 자리로 반올림하면 실제로 있었던
 * 0.05초 개입이 `0.0초` 가 되어 "길이 없음" 으로 읽힌다.
 */
export function bandLengthText(band: TimeSpan): string {
  const seconds = band.endSeconds - band.startSeconds;
  if (seconds < 1) {
    return `${Math.max(0, seconds).toFixed(2)}초`;
  }
  return formatEpisodeDuration(seconds) ?? "—";
}

/** 타임라인 띠가 쓰는 한 줄 이름. 예) `INTERVENTION 00:00.4–00:02.7` */
export function bandCaption(band: ActionSourceBand): string {
  return `${actionSourceTerm(band.category, band.code).label} ${bandClockText(band)}`;
}

/** 같은 출처 안에서 몇 번째 띠인가. 예) `INTERVENTION 2/9` */
export function bandOrdinalName(band: ActionSourceBand): string {
  return `${actionSourceTerm(band.category, band.code).label} ${band.ordinal}/${band.ordinalCount}`;
}

/**
 * 출처 칩의 툴팁·접근성 이름에 쓰는 한 문장.
 * 예) `INTERVENTION 2/9 · 00:02.7–00:07.1 · 4.3초`
 */
export function bandDetailText(band: ActionSourceBand): string {
  return `${bandOrdinalName(band)} · ${bandClockText(band)} · ${bandLengthText(band)}`;
}

/**
 * 후보 확인 기록 한 구절. 예) `확인 김리뷰 · 2026-09-28 11:20`
 *
 * 확인한 사람이 삭제되었으면 이름을 빼고 시각만 적는다.
 */
export function freezeCandidateCheckText(check: QualityCandidateCheckResp): string {
  const who = check.checked_by_member?.display_name;
  const when = formatDateTime(parseUtcMs(check.checked_at));
  return who === undefined ? `확인 · ${when}` : `확인 ${who} · ${when}`;
}

/**
 * 영상 정지 후보 칩의 툴팁·접근성 이름과 재생 바 행의 구간 이름에 쓰는 한 문장. 출처 칩과 같은
 * 형식이다. 확인한 후보면 확인 기록을 끝에 붙인다.
 * 예) `영상 정지 후보 1/2 · 00:20.0–00:29.0 · 9.0초 · 확인 김리뷰 · 2026-09-28 11:20`
 */
export function freezeCandidateDetailText(span: QualityCandidateSpan): string {
  const text = `${FREEZE_CANDIDATE_NAME} ${span.ordinal}/${span.ordinalCount} · ${bandClockText(
    span,
  )} · ${bandLengthText(span)}`;
  return span.check === null ? text : `${text} · ${freezeCandidateCheckText(span.check)}`;
}

/**
 * 영상 정지 후보를 재생 바 행 하나로 바꾼다. 후보가 있는 에피소드에서만 이 행이 생긴다. 그래서
 * 얇게(`rail`) 그려 바 높이가 에피소드마다 덜 달라지게 한다. 색은 칩 점과 같은 `dataViz.blue` 다.
 */
export function freezeCandidateTimelineTrack(
  spans: readonly QualityCandidateSpan[],
): SegmentTimelineTrack {
  return {
    id: QUALITY_CANDIDATE_TRACK_ID,
    type: "track",
    variant: "rail",
    label: FREEZE_CANDIDATE_NAME,
    segments: spans.map((span) => ({
      id: span.id,
      startSeconds: span.startSeconds,
      endSeconds: span.endSeconds,
      tone: "dataViz.blue",
      label: freezeCandidateDetailText(span),
    })),
  };
}

/**
 * 모델을 타임라인 행(출처 띠 한 줄)으로 바꾼다. 이 행이 재생 위치와 seek 도 처리한다.
 * 그래서 재생 행을 따로 두지 않는다.
 */
export function actionSourceTimelineTracks(
  model: ActionSourceModel,
): readonly SegmentTimelineTrack[] {
  return [
    {
      id: ACTION_SOURCE_TRACK_ID,
      type: "track",
      label: "action_source 구간",
      segments: model.bands.map((band) => ({
        id: band.id,
        startSeconds: Math.max(0, band.startSeconds),
        endSeconds: band.endSeconds,
        tone: TONE[actionSourceTerm(band.category, band.code).dataViz],
        label: bandCaption(band),
      })),
    },
  ];
}
